import React, { useState, useEffect, useCallback } from "react";

import { useRecoilValue } from "recoil";
import { userCredentials } from "recoilState/GlobalState";
import XGrid from "components/UI/Grid/XGrid";
import { HeaderButton, Buttons } from "components";
import Modal from "components/UI/Modal/Modal";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";

import { DeleteModal, InsertModal, UpdateModal } from "./Actions";
import { DIAS, paramsADias } from "../consts";
import { RNHHorarioRangoGet } from "services/services-mia/programa-salud/personal/RNHHorarioRangoGet";

import { rangosTitles as titles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";

import styles from "./HorarioRangos.module.scss";

// La marca de "este día aplica". Un tilde y no un "Sí": la columna mide 58px y lo
// que se lee de un vistazo es la forma, no la palabra.
const MARCA = "✓";

/**
 * Alto de la grilla, en píxeles, según cuántas franjas hay.
 *
 * `XGrid` va con `autoPageSize`: llena el alto que le den y calcula cuántas filas
 * entran. O sea que el alto NO lo puede poner el contenido, hay que decírselo — y un
 * alto fijo deja filas vacías abajo cuando el horario tiene una sola franja.
 *
 * El piso de 3 filas es para que el cartel de "Sin datos" y el header tengan dónde
 * dibujarse; el techo de 8 es para que un horario con muchas franjas pagine en vez de
 * estirar el modal hasta el borde de la pantalla.
 */
const ALTO_HEADER = 56;
const ALTO_FILA = 36;
const ALTO_FOOTER = 52;
const FILAS_MINIMAS = 3;
const FILAS_MAXIMAS = 8;

const altoDeGrilla = (cantidad) => {
    const filas = Math.min(Math.max(cantidad, FILAS_MINIMAS), FILAS_MAXIMAS);
    return ALTO_HEADER + ALTO_FOOTER + ALTO_FILA * filas;
};

/**
 * Rangos de un horario: las franjas concretas, con su entrada, su salida y los días en
 * los que aplican.
 *
 * NO es una pantalla. Vive siempre adentro del modal que abre la vista de Horarios
 * (`horarios/Actions/RangoModal`), porque un rango no significa nada sin su horario
 * padre.
 *
 * Pide sus propias franjas con `RNHHorarioRangoGet` filtrando por el horario, y no las
 * lee del que le pasan. Así se refresca sola después de cada alta, modificación o baja
 * sin depender de que la grilla de arriba vuelva a traer todo. `onCambio` avisa hacia
 * arriba, que es otra cosa: la columna de chips de Horarios también quedó vieja.
 */
export default function HorarioRangos({ horario, onCambio }) {
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [isLoading, setIsLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });

    const credentials = useRecoilValue(userCredentials);
    const { service } = useService();
    const puedeEscribir = puedeEditarServicio(service);

    const horarioId = horario?.id;

    const updateList = useCallback(async () => {
        if (!horarioId) return;
        setRows([]);
        setRowSelected({ status: false, row: {} });
        setIsLoading(true);

        const respuesta = await RNHHorarioRangoGet(credentials, { horarioId });
        setIsLoading(false);
        if (!respuesta.ok) return;

        setRows(
            respuesta.data
                // Fila centinela: sin registros el back manda una con todo en null.
                .filter((d) => d?.rnhHorarioRangoId)
                // Por hora de entrada, que es el orden en que transcurre el día.
                .sort((a, b) =>
                    String(a.rnhHorarioRangoEntrada).localeCompare(
                        String(b.rnhHorarioRangoEntrada)
                    )
                )
                .map((d) => {
                    // Los siete campos Y/N del back vuelven a ser el array del front.
                    const dias = paramsADias(d);
                    return {
                        id: d.rnhHorarioRangoId,
                        // La piden el Upd y el Del; no es columna.
                        version: d.rnhHorarioRangoVersion,
                        cod: d.rnhHorarioRangoCod ?? "",
                        nombre: d.rnhHorarioRangoNombre ?? "",
                        horario: horario?.nombre ?? "",
                        // Llegan como 'HH:MM:SS'; en la celda los segundos sobran.
                        entrada: String(d.rnhHorarioRangoEntrada ?? "").substring(0, 5),
                        salida: String(d.rnhHorarioRangoSalida ?? "").substring(0, 5),
                        dias,
                        // Una columna por día: el `field` ES el código del día, así que
                        // la fila se arma sin tabla de traducción en el medio.
                        ...Object.fromEntries(
                            DIAS.map(({ cod }) => [cod, dias.includes(cod) ? MARCA : ""])
                        ),
                    };
                })
        );
    }, [credentials, horarioId, horario]);

    useEffect(() => {
        updateList();
    }, [updateList]);

    // Refresca lo de acá y avisa hacia arriba: son dos cosas distintas y las dos tienen
    // que pasar después de cada operación.
    const alCambiar = () => {
        updateList();
        onCambio?.();
    };

    const handleClick = ({ procede, title }) =>
        setModalDetails({ title, procede, isOpen: true });

    const closeModal = () => setModalDetails({ isOpen: false });

    const limpiarSeleccion = () => setRowSelected({ status: false, row: {} });
    const hayElegido = Boolean(rowSelected.row?.id);

    return (
        <main className={styles.contenedor}>
            {/* `sm` (420px) para los tres: es el mas chico de la escala y alcanza.
                OJO con el selector de dias: son siete botones repartiendose el ancho,
                asi que ahi quedan al limite. Si alguna vez se parten en dos renglones,
                las salidas son volver a `md` o recortarles el padding lateral. */}
            <Modal
                size="sm"
                onClose={closeModal}
                open={modalDetails.isOpen}
                title={modalDetails.title}
            >
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        horario={horario}
                        updateList={alCambiar}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <UpdateModal
                        horarioRango={rowSelected.row}
                        updateList={alCambiar}
                        closeModal={closeModal}
                        onSuccess={limpiarSeleccion}
                    />
                )}
                {modalDetails.procede === ACTIONS.DELETE && (
                    <DeleteModal
                        horarioRango={rowSelected.row}
                        updateList={alCambiar}
                        closeModal={closeModal}
                        onSuccess={limpiarSeleccion}
                    />
                )}
            </Modal>

            <div className={styles.acciones}>
                <Buttons>
                    <HeaderButton
                        text={"Actualizar"}
                        onClick={updateList}
                        disabled={isLoading}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Agregar Rango"}
                        handleClick={handleClick}
                        data={{ title: "Agregar Rango Horario", procede: ACTIONS.INSERT }}
                        disabled={hayElegido || !puedeEscribir}
                    >
                        <Add />
                    </HeaderButton>
                    <HeaderButton
                        text={"Editar Rango"}
                        handleClick={handleClick}
                        data={{ title: "Editar Rango Horario", procede: ACTIONS.UPDATE }}
                        disabled={!hayElegido || !puedeEscribir}
                    >
                        <EditOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={"Eliminar Rango"}
                        handleClick={handleClick}
                        data={{ title: "Eliminar Rango Horario", procede: ACTIONS.DELETE }}
                        disabled={!hayElegido || !puedeEscribir}
                    >
                        <DeleteOutline />
                    </HeaderButton>
                </Buttons>
            </div>

            <div
                className={styles.grilla}
                // Hay que decirle el alto: con `autoPageSize` la grilla llena lo que le
                // den, no se achica sola.
                style={{ height: altoDeGrilla(rows.length) }}
            >
                <XGrid
                    rows={rows}
                    titles={titles}
                    loading={isLoading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}
